import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import UserService from 'services/user/UserService'
import { PAGE_URL } from 'core/constants/path'
import { redirectToMainApp } from 'core/utils/auth-mode'
import { logger } from 'core/utils/logger'
import type { RootState } from 'redux/store'
import { useTelemetry } from 'core/hooks/data/useTelemetry'
import { fetchAuthSession } from 'aws-amplify/auth'
import { getCognitoSubFromSession } from 'core/utils/cognito-sub'
import { analyticsService } from 'lib/insights'

/**
 * Normalizes a redirect parameter which can be either a tenant strid or domain.
 * - Strips protocol (http/https) and trailing slashes
 * - Leaves pure strid values as-is
 */
function normalizeRedirectParam(raw: string): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  // If contains protocol, strip it
  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '')
  // Remove trailing slash
  return withoutProtocol.replace(/\/$/, '')
}

/**
 * Removes the `redirect` query param from the current URL without reloading the page.
 */
function clearRedirectFromUrlInternal(): void {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('redirect')
    const newSearch = url.searchParams.toString()
    const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash
    window.history.replaceState({}, '', newUrl)
  } catch {
    // no-op
  }
}

export function useValidateRedirectTenant() {
  const navigate = useNavigate()
  const userInfo = useSelector((state: RootState) => state.userInfo.userInfo)
  const { identify, track } = useTelemetry()

  const validateAndRedirect = useCallback(
    async (redirectParam: string): Promise<'redirected' | 'no-match'> => {
      const normalized = normalizeRedirectParam(redirectParam)
      if (!normalized) {
        // Nothing to do
        return 'no-match'
      }

      try {
        // Small delay can help ensure session is fully ready in some flows
        await new Promise((resolve) => setTimeout(resolve, 300))

        const userService = new UserService()

        // Fetch user info to get IDs for Mixpanel identity bridge to main app
        let tenantUserId: string | undefined
        let canonicalUserId: string | undefined
        try {
          const session = await fetchAuthSession({ forceRefresh: false })
          canonicalUserId = getCognitoSubFromSession(session) || undefined
        } catch {
          canonicalUserId = undefined
        }

        let userInfoResponse: any = null
        try {
          userInfoResponse = await userService.getUserInfo()
          tenantUserId = userInfoResponse?.strid
          const effectiveLegacyUserId = canonicalUserId || tenantUserId

          // Identify user in Mixpanel if we have user info
          // This is the main place where identify() is called in the redirect flow
          if (userInfoResponse && effectiveLegacyUserId) {
            const userProperties = {
              email: userInfoResponse.email,
              name: userInfoResponse.name,
              company: userInfoResponse.company,
              position: userInfoResponse.position,
              user_tier: userInfoResponse.user_tier,
              workspace_strid: userInfoResponse.workspace_strid,
              tenant_user_id: tenantUserId,
              mixpanel_canonical_id: effectiveLegacyUserId,
              mixpanel_id_source: canonicalUserId
                ? 'cognito_sub'
                : 'tenant_user_id',
            }

            identify(effectiveLegacyUserId, userProperties)

            if (canonicalUserId) {
              analyticsService.identifyOrSetProperties(canonicalUserId, {})
            } else {
              console.warn(
                '[MIXPANEL][V2] Missing cognito_sub during redirect validation. Skipping V2 identify.'
              )
            }
          }
        } catch (error) {
          console.warn(
            '[VALIDATE_REDIRECT] Failed to fetch user info, continuing without userId:',
            error
          )
        }

        const tenantResponse = await userService.getTenantInfo()

        // Determine if value looks like a strid (no protocol/domain tokens)
        const isStrid = !normalized.includes(':') && !normalized.includes('.')

        const matchingTenant = isStrid
          ? tenantResponse.results?.find(
              (t: any) => t.tenant.strid === normalized
            )
          : tenantResponse.results?.find(
              (t: any) => t.tenant.domain === normalized
            )

        if (matchingTenant) {
          const bridgeUserId = canonicalUserId ?? undefined
          track('redirect_to_main_app_from_validate_redirect_tenant', {
            tenantId: matchingTenant.tenant.name,
            tenantDomain: matchingTenant.tenant.domain,
            userId: bridgeUserId,
            tenantUserId: tenantUserId || userInfo?.strid,
            canonicalUserId: canonicalUserId ?? null,
          })
          redirectToMainApp({
            tenantId: matchingTenant.tenant.name,
            tenantDomain: matchingTenant.tenant.domain,
            userId: bridgeUserId,
          })
          try {
            sessionStorage.removeItem('auth:redirectInProgress')
          } catch {}
          return 'redirected'
        } else if (tenantResponse.results?.length > 1) {
          navigate(PAGE_URL.MAIN, { replace: true })
          return 'redirected'
        } else if (tenantResponse.results?.length === 1) {
          const bridgeUserId = canonicalUserId ?? undefined
          track('redirect_to_main_app_from_validate_redirect_tenant', {
            userId: bridgeUserId,
            tenantUserId: tenantUserId || userInfo?.strid,
            canonicalUserId: canonicalUserId ?? null,
          })
          redirectToMainApp({
            userId: bridgeUserId,
          })
          return 'redirected'
        }
      } catch (error) {
        logger.error('Error validating redirect tenant', error)
      }

      // On no match or error, clear redirect and go to root
      // clearRedirectFromUrlInternal()
      // navigate('/', { replace: true })
      try {
        sessionStorage.removeItem('auth:redirectInProgress')
      } catch {}
      return 'no-match'
    },
    [navigate, userInfo?.strid, identify]
  )

  const clearRedirectFromUrl = useCallback(() => {
    clearRedirectFromUrlInternal()
  }, [])

  return {
    validateAndRedirect,
    clearRedirectFromUrl,
  }
}
